import type { adminDe } from './admin.de';
import type { Shape } from './en';

export const adminEn: Shape<typeof adminDe> = {
  title: 'Administration',
  description: 'Accounts, permissions and settings of {{company}}.',
  areas: { company: 'Company', platform: 'Platform' },
  platformHint: 'Applies to all companies of the group.',
  sections: {
    accounts: { label: 'Accounts', description: 'People of the company, locks and permissions.' },
    groups: { label: 'Groups', description: 'Groups of people, own ones and from SSO.' },
    roles: { label: 'Roles', description: 'Bundles of permissions and who has them.' },
    settings: { label: 'Settings', description: 'Defaults for everyone in the company.' },
    sso: {
      label: 'Sign-in',
      description: 'SSO provider of the company and sign-in with password.',
    },
    notifications: {
      label: 'Notifications',
      description: 'E-mail, Teams and push messages as channels of the company.',
    },
    integrations: {
      label: 'Integrations',
      description: 'SharePoint as storage for documents of the modules.',
    },
    customFields: { label: 'Custom fields', description: 'Own fields for module entries.' },
    shares: { label: 'Shares', description: 'Entries other companies may read.' },
    privacy: { label: 'Data protection', description: 'Retention periods, access and erasure.' },
    ai: { label: 'AI', description: 'Provider, data level and AI functions of the company.' },
    branding: { label: 'Appearance', description: 'Logo and company colour.' },
    audit: { label: 'Audit log', description: 'Who changed what and when.' },
    companies: { label: 'Companies', description: 'Companies and subgroups of the group.' },
    modules: {
      label: 'Modules',
      description: 'Default for all companies and switches per company.',
    },
    companyModules: {
      label: 'Modules',
      description: 'Which modules the company uses and in which order.',
    },
    platformAccess: {
      label: 'Platform permissions',
      description: 'Superadmins, platform permissions and linked accounts.',
    },
    platformAudit: {
      label: 'Platform audit',
      description: 'Changes to companies, modules and platform permissions.',
    },
    operations: {
      label: 'Operations',
      description: 'State of server, database, jobs and services.',
    },
  },
  errors: {
    'version-conflict':
      'Someone else changed this in the meantime. The page now shows the current state, please try again.',
    'rate-limited': 'Too many requests. Please wait a moment and try again.',
    forbidden: 'A permission is missing for this.',
    'invalid-input': 'Please check the entries.',
    notFound: 'No longer there. Someone may have deleted it.',
  },
  accounts: {
    title: 'Accounts',
    description:
      'Accounts are created at the first sign-in through SSO, or here for the sign-in with password.',
    search: 'Name or email',
    statusFilter: 'Status',
    allStatuses: 'All',
    columns: {
      name: 'Name',
      email: 'Email',
      login: 'Sign-in',
      status: 'Status',
      lastLogin: 'Last sign-in',
    },
    status: { active: 'Active', locked: 'Locked' },
    lockReason: { admin: 'locked by the administration', provider: 'locked by the SSO provider' },
    never: 'never',
    empty: 'No accounts found',
    emptyHint: 'Change the search or remove the filter.',
    loadMore: 'Load more',
    create: 'Create account',
    createHint:
      'For people without SSO. They get an invitation by email and set their password with it.',
    name: 'Name',
    loginEmail: 'Email address',
    loginEmailHint: 'The person signs in with it. The invitation goes to this address.',
    emailInvalid: 'Please enter a complete email address.',
    invite: 'Create and invite',
    created: 'Account created, the invitation is on its way.',
    createdWithoutMail:
      'Account created, but the invitation was not sent. Send it again on the page of the account.',
    login: {
      sso: 'SSO',
      password: 'Password',
      invited: 'Invited',
      twoFactor: 'Authenticator',
      passkey: 'Passkey',
      none: 'None',
    },
    errors: {
      'account-locked': 'The account is locked. Unlock it first.',
      'cannot-reset-self':
        'Your own authenticator app is removed in the profile, not in the administration.',
      'login-email-taken': 'An account of the company already signs in with this email address.',
      'mail-not-sent': 'The email was not sent. Please send it again later.',
      'no-email': 'No email address is known for the account.',
      'password-login-disabled':
        'The company signs in with SSO only. Sign-in with password can be allowed under Sign-in.',
      'platform-account':
        'The account has permissions on the whole platform. Only a superadmin sends it links or resets its app.',
    },
    detail: {
      account: 'Account',
      createdAt: 'Created',
      ssoManaged: 'Name and email come from the SSO of the company and are changed there.',
      appManaged: 'Account with sign-in by password, created in the STAW App.',
      login: 'Sign-in',
      loginHint: 'Password links go by email to the person, never to the administration.',
      ssoYes: 'Through the SSO provider of the company',
      ssoNo: 'No',
      loginEmail: 'Email address for the sign-in',
      passwordState: { set: 'Set', invited: 'Invitation open', none: 'None' },
      twoFactor: 'Authenticator app',
      twoFactorOn: 'Set up',
      twoFactorOff: 'Not set up',
      passkeys: 'Passkeys',
      passkeyCount_one: '{{count}} passkey',
      passkeyCount_other: '{{count}} passkeys',
      passkeysNone: 'None',
      sendInvite: 'Send invitation',
      sendReset: 'Send reset link',
      linkSent: 'Link sent by email to {{email}}',
      addPasswordTitle: 'Also sign in with a password?',
      addPasswordHint:
        'The account comes from SSO. With the link to {{email}} it also signs in with email address and password, as long as the company allows that next to SSO.',
      resetTwoFactor: 'Reset second factor',
      resetTwoFactorTitle: 'Remove the authenticator app and passkeys of {{name}}?',
      resetTwoFactorHint:
        'For a person who lost their phone or password manager, or when someone else added a passkey. Their sessions end, they sign in with the password only and set up the app and passkeys again in the profile. They get an email about it.',
      twoFactorRemoved: 'Authenticator app and passkeys removed',
      groups: 'Groups',
      noGroups: 'Not in any group.',
      addGroup: 'Add to group',
      groupAdded: 'Added to {{group}}',
      removeGroup: 'Remove from {{group}}',
      groupRemoved: 'Removed from {{group}}',
      addRole: 'Assign role',
      chooseRole: 'Choose role',
      searchRole: 'Search role',
      removeRole: 'Remove role {{role}}',
      roles: 'Roles',
      noRoles: 'No role assigned. The person only sees what needs no permission.',
      direct: 'Assigned directly',
      viaGroup: 'Through group {{group}}',
      viaEveryone: 'For every account of the company',
      until: 'until {{date}}',
      permissions: 'Permissions',
      permissionsHint: 'What the person may do and which role it comes from.',
      noPermissions: 'No permissions.',
      from: 'from {{roles}}',
      lock: 'Lock account',
      unlock: 'Unlock',
      lockTitle: 'Lock the account of {{name}}?',
      lockDescription: 'All sessions end at once. Signing in works again after unlocking.',
      locked: 'Account locked',
      unlocked: 'Account unlocked',
      cannotLockSelf: 'Your own account cannot be locked.',
    },
    privacy: {
      title: 'Data protection',
      description: 'Requests of the person under Art. 15, 20 and 17 GDPR.',
      exportHint:
        'All data the STAW App keeps about this person, from the core and all modules, as a JSON file.',
      export: 'Export data',
      exported: 'Export downloaded. The audit log records it.',
      fileName: 'personal-data-{{date}}.json',
      eraseHint:
        'Deletes or anonymises all data of the person. The account stays without name and e-mail, so history and audit log remain consistent.',
      eraseLocked: 'Only locked accounts can be erased. Lock the account first.',
      eraseSelf: 'Your own account cannot be erased.',
      erase: 'Erase person',
      eraseTitle: 'Erase all data of {{name}}?',
      eraseDescription:
        'The core and the modules delete or anonymise all data about this person. This cannot be undone. Data with a legal retention period stays until it ends.',
      erased: 'Person erased',
    },
  },
  groups: {
    title: 'Groups',
    description: 'Give roles to a group instead of to every person.',
    new: 'New group',
    columns: { name: 'Name', members: 'Members', source: 'Source' },
    source: { local: 'Own', provider: 'SSO' },
    empty: 'No groups yet',
    emptyHint: 'Groups bundle people, e.g. all project leads.',
    name: 'Name',
    descriptionLabel: 'Description',
    create: 'Create group',
    created: 'Group created',
    edit: 'Edit',
    editTitle: 'Edit group',
    saved: 'Saved',
    delete: 'Delete group',
    deleteTitle: 'Delete group {{name}}?',
    deleteDescription: 'Members lose the roles assigned through this group.',
    deleted: 'Group deleted',
    members: 'Members',
    memberCount_one: '{{count}} member',
    memberCount_other: '{{count}} members',
    noMembers: 'No members yet.',
    addMember: 'Add person',
    choosePerson: 'Choose person',
    searchPerson: 'Name or email',
    memberAdded: '{{name}} added',
    removeMember: 'Remove {{name}} from the group',
    memberRemoved: '{{name}} removed',
    providerManaged: 'This group comes from SSO. Name and members are maintained there.',
  },
  roles: {
    title: 'Roles',
    description: 'A role bundles permissions. It is given to people or groups.',
    new: 'New role',
    report: 'Recertification',
    columns: { name: 'Name', permissions: 'Permissions', assignments: 'Assignments' },
    template: 'Template',
    empty: 'No roles yet',
    name: 'Name',
    descriptionLabel: 'Description',
    templateHint: 'Template of the STAW App. Name and permissions can be changed.',
    permissions: 'Permissions',
    permissionsHint: 'What the role allows. Some permissions also take a scope.',
    searchPermissions: 'Search permissions',
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
    allOf: 'All permissions of this area, including future ones',
    scope: 'Scope',
    scopes: { own: 'Own', team: 'Team', 'org-unit': 'Org unit', all: 'All' },
    privileged: 'Admin permission',
    privilegedHint: 'Only effective when signed in with MFA.',
    sensitive: 'Sensitive',
    sensitiveHint: 'Every access is logged.',
    noPermissions: 'No permissions found.',
    create: 'Create role',
    created: 'Role created',
    save: 'Save changes',
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    discard: 'Discard',
    delete: 'Delete role',
    deleteTitle: 'Delete role {{name}}?',
    deleteDescription: 'All assignments of this role end at once.',
    deleted: 'Role deleted',
    assignments: 'Assignments',
    assignmentsHint: 'Who has this role. Assignments with an end date expire on their own.',
    noAssignments: 'Not assigned to anyone yet.',
    assign: 'Assign',
    assignTitle: 'Assign role {{name}}',
    assignTo: 'Assign to',
    person: 'Person',
    group: 'Group',
    everyone: 'All accounts',
    everyoneHint: 'Every account of the company, also accounts added later.',
    chooseGroup: 'Choose group',
    searchGroup: 'Search group',
    validUntil: 'Valid until',
    validUntilHint: 'Leave empty for no end, fill in e.g. for holiday cover.',
    unlimited: 'no end',
    assigned: 'Assigned',
    unassign: 'Remove assignment to {{name}}',
    unassigned: 'Assignment removed',
    invalidPermission: 'A permission no longer fits. Please check the selection.',
    notForEveryone:
      'The role applies to all accounts. Administrative and sensitive permissions are not allowed for that.',
    owners: { company: 'Company administration', core: 'General' },
    reportTitle: 'Recertification',
    reportDescription:
      'All active assignments. Basis for the regular review of who has which permissions (ISO 27001).',
    reportColumns: {
      assignee: 'Assigned to',
      role: 'Role',
      privileged: 'Admin permissions',
      since: 'Since',
      until: 'Until',
    },
    onlyPrivileged: 'Only with admin permissions',
    selfAssigned: 'Self-assigned',
    noReport: 'No active assignments.',
  },
  settings: {
    title: 'Settings',
    description:
      'Defaults for everyone in the company. Personal settings apply where someone chose for themselves.',
    companyDefault: 'Company default',
    removeDefault: 'Remove default',
    saved: 'Saved',
    empty: 'No settings released for changing.',
    sections: {
      company: 'Company',
      personalDefaults: 'Defaults for personal settings',
      platform: 'All companies',
    },
    modules: {
      title: 'Module settings',
      description: 'Defaults, catalogs and templates of a module are on its settings page.',
    },
  },
  notifications: {
    title: 'Notifications',
    description:
      'Channels that point the people of the company to new notifications. Everyone chooses in their profile.',
    neutralHint:
      'Messages outside the app only name the number of unread notifications and link the app, never their content.',
    saved: 'Saved',
    save: 'Save',
    test: 'Send test to me',
    testSent: 'Test sent',
    secretStored: 'Stored. Leave empty to keep it.',
    secretMissing: 'Not stored yet.',
    hostInvalid: 'Please enter a server name, e.g. smtp.company.com.',
    portInvalid: 'Port from 1 to 65535.',
    emailInvalid: 'Please enter a valid e-mail address.',
    idInvalid: 'Please enter an ID like 00000000-0000-0000-0000-000000000000.',
    email: {
      title: 'E-mail',
      description: 'The test goes to the own address and uses the saved setup.',
      modes: {
        platform: 'Server of the STAW App',
        platformHint: 'Sent through the shared mail server of the group.',
        platformMissing: 'This installation has no shared mail server.',
        own: 'Own SMTP server',
        ownHint: 'Sent through the mail server of the company, e.g. with an own sender address.',
        off: 'No e-mail',
      },
      host: 'Server',
      port: 'Port',
      security: 'Encryption',
      username: 'User name',
      password: 'Password',
      fromAddress: 'Sender address',
      fromName: 'Sender name',
    },
    teams: {
      title: 'Microsoft Teams',
      description: 'Notices in the Teams activity feed, for accounts from Microsoft Entra ID.',
      enabled: 'Offer Teams notices',
      setupHint:
        'Needs an app registration in Entra ID with the application permission TeamsActivity.Send and the Teams app of the STAW App, deployed to everyone. The steps are in the guide for this page.',
      tenantId: 'Directory (tenant) ID',
      clientId: 'Application (client) ID',
      clientSecret: 'Client secret',
      appId: 'ID of the Teams app',
      appIdHint: 'From the manifest of the Teams app, not the application ID.',
    },
    push: {
      title: 'Push messages',
      description:
        'Notices in the notification centre of iPhone, iPad, Android, Windows and macOS. The message is encrypted up to the device.',
      enabled: 'Offer push messages',
      enabledHint:
        'People switch them on in their profile, for each device. The test goes to your own devices.',
    },
    errors: {
      'not-configured': 'This channel is not set up or saved yet.',
      'no-address': 'The own account has no address for this channel.',
      'auth-failed': 'Signing in to the service failed. Check the credentials.',
      'connection-failed': 'The server cannot be reached.',
      rejected: 'The service refused the message.',
      'teams-incomplete': 'Teams is missing details.',
      'smtp-missing': 'The own server is missing details.',
      'password-required': 'Server, port or user name changed. Please enter the password again.',
      'secret-required': 'Tenant or application ID changed. Please enter the client secret again.',
    },
  },
  integrations: {
    title: 'Integrations',
    description: 'Services of other providers the company connects to the STAW App.',
    saved: 'Saved',
    save: 'Save',
    test: 'Test connection',
    testSucceeded: 'The connection to {{site}} works.',
    secretStored: 'Stored. Leave empty to keep it.',
    secretMissing: 'Not stored yet.',
    idInvalid: 'Please enter an ID like 00000000-0000-0000-0000-000000000000.',
    sharepoint: {
      title: 'SharePoint',
      description:
        'Modules keep documents in a library of the company, each entry in a folder of its own. Without SharePoint they stay in the file storage of the STAW App.',
      enabled: 'Keep documents in SharePoint',
      enabledHint: 'Saving checks the connection first.',
      verified: 'Connected to {{site}}, last checked {{time}}.',
      unverified: 'The connection has not been checked yet.',
      setupHint:
        'Needs an app registration in Entra ID with the Graph application permission Sites.Selected and write access to this site. The guide of this page lists the steps.',
      tenantId: 'Directory (tenant) ID',
      clientId: 'Application (client) ID',
      credential: 'Sign-in of the app',
      credentials: {
        certificate: 'Certificate',
        certificateHint:
          'Recommended. Entra ID only knows the public part, the private key is kept encrypted in the STAW App.',
        secret: 'Client secret',
        secretHint: 'Expires after two years at most and must then be entered again.',
      },
      clientSecret: 'Client secret',
      privateKey: 'Private key of the certificate (PEM)',
      thumbprint: 'Certificate thumbprint',
      thumbprintHint: 'As Entra ID shows it under “Certificates & secrets”, 40 characters.',
      thumbprintInvalid: 'Please enter the 40 characters of the thumbprint.',
      site: 'Site',
      siteHint: 'Address of the site, e.g. https://company.sharepoint.com/sites/Sales',
      siteInvalid: 'Please enter the address of a SharePoint site starting with https://.',
      library: 'Library',
      libraryHint: 'Name of the document library, e.g. Documents.',
      rootFolder: 'Root folder',
      rootFolderHint:
        'Folder in the library below which the modules create their folders. Empty for the top level.',
      folderInvalid: 'Folder names without " * : < > ? \\ |, levels separated by /.',
    },
    errors: {
      'auth-failed':
        'Signing in at Microsoft failed. Check the tenant ID, the application ID and the key.',
      forbidden: 'The app has no access to the site. Check the grant for Sites.Selected.',
      'site-not-found': 'This site does not exist.',
      'library-not-found': 'The site has no library with this name.',
      'folder-invalid': 'The root folder is invalid, or a file with its name is in the way.',
      'connection-failed': 'Microsoft cannot be reached right now. Please try again later.',
      incomplete: 'The connection lacks details. Save them completely first, then test.',
      'site-invalid': 'Please enter the address of a SharePoint site.',
      'private-key-invalid':
        'The private key must be an RSA key in PEM format with at least 2048 bits.',
      'thumbprint-invalid': 'Please enter the 40 characters of the thumbprint.',
      'secret-required':
        'The tenant or application ID changed. Please enter the secret or the certificate again.',
    },
  },
  sso: {
    title: 'Sign-in',
    description:
      'People of the company sign in through the SSO provider of the company, usually Microsoft Entra ID, or with email address and password.',
    status: { draft: 'Draft', active: 'Active', disabled: 'Disabled' },
    notConfigured: 'No provider set up yet.',
    provider: 'Provider',
    issuer: 'Issuer URL',
    issuerHint: 'For Entra ID: https://login.microsoftonline.com/<tenant ID>/v2.0',
    issuerInvalid: 'An address starting with https://.',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    secretStored: 'A secret is stored. Leave empty to keep it.',
    secretMissing: 'Without a secret only for providers that allow it (public client with PKCE).',
    scopes: 'Scopes',
    scopesHint: 'Separated by spaces, starting with openid.',
    redirectUri: 'Redirect URI',
    redirectUriHint: 'Enter it in the app registration at the provider.',
    copy: 'Copy',
    copied: 'Copied',
    save: 'Save',
    saved: 'Saved. Activate with a test sign-in.',
    test: 'Test sign-in',
    testHint: 'The provider becomes active only after a successful test sign-in.',
    activeHint: 'Every change sets the provider back to draft until a test sign-in succeeds.',
    verifiedAt: 'Last successful test',
    passwordLogin: {
      title: 'Sign-in with password',
      label: 'Next to SSO also with email address and password',
      description:
        'For people without an account at the SSO provider, e.g. external staff. Switched off, everyone signs in through SSO.',
      inactive:
        'As long as no SSO provider is active, everyone signs in with email address and password.',
      enabled: 'Sign-in with password allowed',
      disabled: 'Sign-in with password ended',
      offTitle: 'End sign-in with password?',
      offDescription:
        'All sessions with a password end at once. Accounts without SSO only get into the app again once sign-in with password is allowed again.',
      off: 'End',
      errors: {
        'sso-not-active': 'Activate the SSO provider with a test sign-in first.',
        'sso-session-required':
          'Sign in with SSO to switch this off. That makes sure the administration still gets in afterwards.',
      },
    },
    disable: 'Disable',
    disableTitle: 'Disable SSO sign-in?',
    disableDescription:
      'Afterwards everyone signs in with email address and password. Accounts from SSO first need a password link from their account page. Running sessions remain.',
    disabled: 'Disabled',
    protocol: 'Protocol',
    protocols: {
      oidc: {
        label: 'OpenID Connect',
        description: 'Default, with Entra ID through an app registration.',
      },
      saml: {
        label: 'SAML 2.0',
        description:
          'For providers that only support or require SAML. With Entra ID through an enterprise application.',
      },
    },
    saml: {
      entityId: 'Entity ID',
      ssoUrl: 'Sign-in URL',
      certificates: 'Signing certificates',
      accounts: 'Accounts',
      accountsShared: 'Same as with OpenID Connect (Entra tenant confirmed by Microsoft)',
      accountsOwn: 'Accounts of their own for this SAML provider',
      fingerprint: 'SHA-256 {{value}}',
      validUntil: 'valid until {{date}}',
      certificateExpiring:
        'A signing certificate expires in less than 30 days. Enter the metadata again as soon as the identity provider has a new one.',
      metadata: 'Metadata of the identity provider',
      metadataKeep: 'Leave empty to keep the stored metadata.',
      metadataRequired: 'Provide the metadata as XML or as URL.',
      source: { xml: 'XML', url: 'URL' },
      sourceHint: {
        xml: 'For Entra ID: "Federation Metadata XML" of the enterprise application, as file or pasted. Sign-in creates accounts of its own, separate from OpenID Connect.',
        url: 'For Entra ID: "App Federation Metadata Url". Only publicly reachable https addresses. If the URL is Microsoft\'s, the accounts are the same as with OpenID Connect.',
      },
      xmlPlaceholder: '<EntityDescriptor …>',
      upload: 'Load XML file',
      attributes: 'Attributes',
      attributesHint:
        'Which attributes of the SAML response hold identifier, name and email. Leave empty for the Entra ID defaults.',
      attribute: { subject: 'Identifier', displayName: 'Name', email: 'Email' },
      subjectHint:
        'Links the sign-in to the account and must never change. Do not adjust it after go-live, otherwise new accounts are created. "NameID" takes the NameID of the response.',
      serviceProvider: 'Enter at the identity provider',
      serviceProviderHint:
        'For Entra ID under "Basic SAML Configuration" as identifier and reply URL.',
      spEntityId: 'Identifier (entity ID)',
      acsUrl: 'Reply URL (assertion consumer service)',
      download: 'Download metadata',
      errors: {
        invalid:
          'The metadata cannot be used. It needs a SAML 2.0 provider, an https sign-in URL and a signing certificate.',
        unreachable: 'The metadata URL is not reachable. Load the XML file instead.',
      },
    },
  },
  branding: {
    title: 'Appearance',
    description: 'Logo and colour of the company. Layout and components stay the same for all.',
    logo: 'Logo',
    logoHint: 'SVG, PNG or WebP up to 512 KB. Square works best.',
    noLogo: 'No logo. The app shows the short name of the company.',
    upload: 'Upload logo',
    replace: 'Replace',
    remove: 'Remove',
    uploaded: 'Logo saved',
    removed: 'Logo removed',
    invalidLogo:
      'This file does not fit. Allowed are SVG without scripts, PNG and WebP up to 512 KB.',
    color: 'Company colour',
    colorHint:
      'For active navigation, highlights and focus. Light and dark variants are derived and checked for contrast.',
    pickColor: 'Pick colour',
    colorEmpty: 'Left empty, the default colour of the STAW App applies.',
    invalidColor: 'Enter the colour as #rrggbb.',
    useDefault: 'Default colour',
    preview: 'Preview',
    previewButton: 'Button',
    previewLink: 'Highlight',
    previewBadge: 'New',
    saved: 'Saved',
  },
  shares: {
    title: 'Shares',
    description:
      'Entries this company shares with other companies, and the other way round. Shared entries are read-only.',
    direction: 'Direction',
    outgoing: 'Shared by us',
    incoming: 'Shared with us',
    untitled: 'Untitled entry',
    columns: {
      record: 'Entry',
      to: 'Shared with',
      from: 'Shared by',
      parts: 'Also shared',
      expires: 'Until',
    },
    emptyOutgoing: 'No entry is shared with another company',
    emptyIncoming: 'No other company shares entries with this one',
    emptyHint: 'Modules that allow it offer sharing on the page of an entry.',
  },
  privacy: {
    title: 'Data protection',
    description:
      'How long the company keeps personal data. The periods of the modules and the core are proposals until the company switches them on here.',
    requestsTitle: 'Access and erasure',
    requestsHint:
      'Requests of a person under Art. 15, 17 and 20 GDPR go through the page of their account under Administration › Accounts.',
    columns: { data: 'Data', period: 'Period', status: 'Status', lastRun: 'Last run' },
    mode: {
      active: 'On',
      off: 'Off',
      automatic: 'Automatic',
      erasure: 'With the person',
    },
    proposal: 'Proposal, from {{start}}',
    after: {
      label: 'from {{start}}',
      'account-locked': 'locking of the account',
      'session-end': 'end of the session',
      event: 'event',
      'account-erased': 'erasure of the person',
      read: 'reading',
      'consent-withdrawn': 'withdrawal of consent',
      'switched-off': 'switched off by the person',
      request: 'request',
      'job-finished': 'end of the job',
    },
    entities: {
      core: {
        account: 'Accounts',
        login: 'Login data (password, authenticator app)',
        session: 'Sessions',
        audit: 'Audit log',
        setting: 'Personal settings',
        notification: 'Notifications',
        'notification-contact': 'Mobile numbers (formerly WhatsApp)',
        'push-subscription': 'Devices for push messages',
        'notification-dispatch': 'Times of sending',
        'calendar-invite': 'Calendar invitations sent',
        event: 'Events between modules',
        'idempotency-key': 'Stored responses',
        'ai-usage': 'Use of AI functions',
        'saved-view': 'Saved views',
        signature: 'Signatures',
        job: 'Background jobs',
      },
    },
    units: {
      D_one: '{{count}} day',
      D_other: '{{count}} days',
      M_one: '{{count}} month',
      M_other: '{{count}} months',
      Y_one: '{{count}} year',
      Y_other: '{{count}} years',
    },
    unitNames: { D: 'Days', M: 'Months', Y: 'Years' },
    lastRun_one: '{{date}}, {{count}} entry',
    lastRun_other: '{{date}}, {{count}} entries',
    edit: 'Change period of {{name}}',
    dialogTitle: 'Retention period: {{name}}',
    dialogDescription: 'Proposal: {{period}}. Agree on it with the data protection officer.',
    enabled: 'Apply period',
    enabledDelete: 'Every night, entries whose period has ended are deleted.',
    enabledAnonymize: 'Every night, entries whose period has ended are anonymised.',
    amount: 'Duration',
    amountInvalid: 'A whole number from 1 to 9999',
    unit: 'Unit',
    minimum: 'At least {{minimum}}',
    tooShort: 'The period must be at least {{minimum}}.',
    warningTitle: 'Cannot be undone',
    warning: 'Anything older is deleted or anonymised for good in the coming night.',
    saved: 'Retention period saved',
  },
  customFields: {
    title: 'Custom fields',
    description:
      'Own fields for module entries, such as a cost centre on a vehicle. They apply to this company only.',
    entity: 'Entry',
    noEntities: 'No active module offers custom fields',
    noEntitiesHint: 'Modules decide which of their entries can be extended with fields.',
    empty: 'No custom fields yet',
    emptyHint: 'Fields appear on the page of every entry, in the order of this list.',
    new: 'Create field',
    editTitle: 'Edit field',
    created: 'Field created',
    saved: 'Field saved',
    removed: '“{{name}}” deleted',
    columns: { label: 'Label', type: 'Kind', required: 'Required' },
    requiredBadge: 'Required',
    labelDe: 'Label (German)',
    labelEn: 'Label (English)',
    type: 'Kind',
    types: {
      text: 'Text, single line',
      textarea: 'Text, multiple lines',
      number: 'Number',
      date: 'Date',
      boolean: 'Yes/no',
      select: 'Choice',
      multiselect: 'Multiple choice',
    },
    key: 'Key',
    keyHint: 'For interfaces and exports. Cannot be changed later, nor can the kind.',
    keyInvalid: 'Lower-case letters, digits and dashes, starting with a letter.',
    required: 'Required field',
    requiredHint: 'Applies the next time an entry is saved, not retroactively.',
    helpDe: 'Help text (German)',
    helpEn: 'Help text (English)',
    bothLanguages: 'Please fill in German and English.',
    options: 'Choices',
    optionDe: 'Choice {{number}}, German',
    optionEn: 'Choice {{number}}, English',
    optionPlaceholderDe: 'German',
    optionPlaceholderEn: 'English',
    addOption: 'Add choice',
    removeOption: 'Remove choice {{number}}',
    optionsRequired: 'Enter at least one choice.',
    optionsHint: 'A removed choice no longer shows in entries that had chosen it.',
    moveUp: 'Move “{{name}}” up',
    moveDown: 'Move “{{name}}” down',
    remove: 'Delete “{{name}}”',
    removeTitle: 'Delete “{{name}}”?',
    removeDescription:
      'The field and its values in all entries of the company are deleted. This cannot be undone.',
    removeConfirm: 'Delete field',
    exists: 'A field with this key already exists.',
    limit: 'More than 50 fields per entry are not possible.',
  },
  audit: {
    title: 'Audit log',
    description: 'Every change and every access to protected data. Entries cannot be changed.',
    platformTitle: 'Platform audit',
    platformDescription: 'Changes to companies, modules, platform permissions and global settings.',
    readHint: 'Reading this log is logged as well.',
    columns: {
      time: 'Time',
      actor: 'Who',
      action: 'Action',
      target: 'Object',
      outcome: 'Outcome',
    },
    outcome: { success: 'Succeeded', denied: 'Denied', failure: 'Failed' },
    system: 'System',
    superadmin: 'Superadmin',
    unknownActor: 'Unknown',
    filters: {
      action: 'Action',
      actionPlaceholder: 'e.g. company.roles.*',
      outcome: 'Outcome',
      all: 'All',
      from: 'From',
      to: 'To',
      reset: 'Reset filters',
    },
    loadMore: 'Load older',
    empty: 'No entries',
    emptyHint: 'There are no entries for these filters.',
    entry: 'Entry',
    occurredAt: 'Time',
    recordedAt: 'Recorded',
    company: 'Company',
    targetType: 'Object type',
    targetId: 'Object ID',
    requestId: 'Request ID',
    details: 'Details',
    selfChange: 'On own account',
    offline: 'Recorded offline',
    actions: {
      account: {
        notifications: {
          'push-on': 'Push messages switched on for a device',
          'push-off': 'Push messages switched off for a device',
        },
      },
      auth: {
        access: 'Access',
        'company-switch': 'Switched company',
        login: 'Signed in',
        logout: 'Signed out',
        passkey: { add: 'Passkey added', remove: 'Passkey removed' },
        password: { change: 'Password changed', reset: 'Password set by link' },
        'two-factor': {
          'backup-codes': 'New backup codes created',
          disable: 'Authenticator app removed',
          enable: 'Authenticator app set up',
        },
      },
      core: {
        catalogs: {
          create: 'Catalog entry created',
          delete: 'Catalog entry deleted',
          import: 'Catalog imported',
          reorder: 'Catalog reordered',
          update: 'Catalog entry changed',
        },
        findings: {
          add: 'Finding recorded',
          copy: 'Findings taken over',
          remove: 'Finding deleted',
          update: 'Finding changed',
        },
        plans: { add: 'Plan added', remove: 'Plan removed', update: 'Plan changed' },
        signatures: {
          read: 'Signature retrieved for a report',
          remove: 'Signature removed',
          update: 'Signature changed',
        },
        templates: {
          create: 'Template created',
          delete: 'Template deleted',
          update: 'Template changed',
        },
        'custom-fields': { read: 'Custom fields viewed', update: 'Custom fields changed' },
        activity: { read: 'History viewed' },
        comments: {
          add: 'Comment written',
          remove: 'Comment deleted',
          update: 'Comment edited',
        },
        documents: {
          add: 'Document stored in SharePoint',
          folder: 'Folder for documents created',
          link: 'SharePoint file linked',
          remove: 'Document removed in SharePoint',
          unlink: 'Link to SharePoint file removed',
        },
        files: {
          add: 'File added',
          download: 'File downloaded',
          'download-zip': 'Files downloaded as ZIP',
          reject: 'File rejected (malware)',
          remove: 'File removed',
        },
        shares: {
          create: 'Shared with another company',
          'incoming-create': 'Share from another company received',
          'incoming-revoke': 'Share from another company ended',
          'incoming-update': 'Share from another company changed',
          revoke: 'Share ended',
          update: 'Share changed',
        },
        sync: { reject: 'Offline change rejected' },
        imports: { apply: 'Data imported' },
        reminders: { add: 'Reminder set', update: 'Reminder changed', remove: 'Reminder deleted' },
        sequences: { update: 'Number sequence changed' },
        tasks: { add: 'Task added', update: 'Task changed', remove: 'Task deleted' },
        privacy: { retention: 'Retention period applied' },
        records: { merge: 'Duplicates merged' },
        views: {
          create: 'View saved for the company',
          delete: 'Company view deleted',
          update: 'Company view changed',
        },
        workflows: { transition: 'Status changed' },
      },
      company: {
        ai: { test: 'AI connection tested', update: 'AI settings changed' },
        accounts: {
          create: 'Account with password created',
          invite: 'Invitation sent',
          link: 'Accounts linked',
          lock: 'Account locked',
          'password-reset': 'Link for a new password sent',
          provision: 'Account created',
          'two-factor-reset': 'Authenticator app reset',
          unlink: 'Link removed',
          unlock: 'Account unlocked',
        },
        audit: { read: 'Audit log read' },
        branding: {
          logo: 'Logo changed',
          'logo-remove': 'Logo removed',
          update: 'Company colour changed',
        },
        'custom-fields': {
          create: 'Custom field created',
          delete: 'Custom field deleted',
          reorder: 'Custom fields reordered',
          update: 'Custom field changed',
        },
        integrations: {
          test: 'SharePoint connection tested',
          update: 'SharePoint connection changed',
        },
        groups: {
          'add-member': 'Member added',
          create: 'Group created',
          delete: 'Group deleted',
          'remove-member': 'Member removed',
          update: 'Group changed',
        },
        modules: { activation: 'Module switched on or off', order: 'Modules reordered' },
        roles: {
          assign: 'Role assigned',
          create: 'Role created',
          delete: 'Role deleted',
          unassign: 'Assignment removed',
          update: 'Role changed',
        },
        settings: { update: 'Setting changed' },
        notifications: {
          test: 'Notification channel tested',
          update: 'Notification channels changed',
        },
        privacy: {
          erase: 'Person erased',
          export: 'Data of a person exported',
          'retention-update': 'Retention period changed',
        },
        sso: {
          disable: 'SSO disabled',
          'password-login': 'Password login switched',
          update: 'SSO changed',
          verify: 'SSO tested and activated',
        },
      },
      platform: {
        audit: { read: 'Platform audit read' },
        backups: { create: 'Database backup requested' },
        companies: { create: 'Company created', update: 'Company changed' },
        grants: { create: 'Platform permission granted', delete: 'Platform permission revoked' },
        modules: { activation: 'Module switched' },
        settings: { update: 'Global setting changed' },
      },
    },
  },
  ai: {
    title: 'AI',
    description:
      'AI functions of the modules send texts to a language model. Here the company decides which provider, which key and which data.',
    enabled: 'AI functions switched on',
    enabledHint: 'Off: modules show no AI buttons and the kernel refuses every call.',
    provider: {
      title: 'Provider',
      description:
        'Anthropic (Claude) directly or OpenRouter, which forwards to many model vendors. OpenRouter only uses vendors that neither train on nor keep requests.',
      label: 'Provider',
      platformKey: 'The platform provides a key, default model {{model}}.',
      noPlatformKey: 'The platform has no key for it. An own key is needed.',
    },
    providers: { anthropic: 'Anthropic (Claude)', openrouter: 'OpenRouter' },
    model: 'Model',
    modelHint: 'For open tasks. Leave empty for the default model of the platform.',
    smallModel: 'Model for simple tasks',
    smallModelHint:
      'For narrowly guided functions such as the finding draft, cheaper and faster. Leave empty for the small model of the platform.',
    smallModelTag: 'small model',
    filesTag: 'reads files',
    effort: {
      title: 'Thinking effort',
      description:
        'How thoroughly the model thinks before it answers. Less effort is cheaper and faster, more effort is more thorough but costs more: thinking uses tokens.',
      standard: 'Open tasks',
      small: 'Simple tasks',
      auto: 'Automatic ({{level}})',
      autoHint: 'Automatic takes what the function asks for, otherwise the value in brackets.',
      levels: { low: 'Low', medium: 'Medium', high: 'High' },
    },
    modelInvalid: 'Model ID, e.g. claude-sonnet-5-5 or vendor/model.',
    apiKey: 'Own API key',
    keyStored: 'A key is stored. Leave empty to keep it.',
    keyOptional: 'Optional. Replaces the key of the platform; the company then bears the costs.',
    keyMissing: 'Required, as the platform has no key for this provider.',
    keyRequired: 'AI cannot be switched on without a key.',
    keyInvalid: 'The key is too short.',
    removeKey: 'Remove own key',
    test: 'Test connection',
    testSucceeded: '{{provider}} answers with model {{model}}.',
    connectionTest: 'Connection test',
    dataTitle: 'Data',
    dataDescription:
      'The highest level functions may send to the provider. The company decides; levels with personal data need a data processing agreement with the provider. Special categories such as health data never go to an external model.',
    data: {
      none: {
        label: 'Without reference to people',
        short: 'No personal data',
        description: 'Only texts without data about people, e.g. help with wording.',
      },
      pseudonymized: {
        label: 'Pseudonymised',
        short: 'Pseudonymised',
        description:
          'Names, email addresses, phone numbers and IBANs are replaced by placeholders before sending and put back into the answer.',
      },
      personal: {
        label: 'Personal',
        short: 'Personal',
        description: 'Data about people goes to the provider in clear text.',
      },
    },
    personalTitle: 'Only with the consent of the data protection officer',
    personalWarning:
      'Personal data leaves the app in clear text. The purpose of every function must be in the record of processing activities.',
    limit: 'Monthly limit (tokens)',
    limitHint:
      'Leave empty for no limit. Beyond it the kernel refuses until the end of the month. Input read from the provider cache counts a tenth, writing to it 1.25.',
    limitInvalid: 'A whole number from 1000.',
    monthCalls: 'Calls this month',
    monthTokens: 'Tokens this month',
    functionsTitle: 'Functions',
    functionsHint:
      'AI functions of the modules with the data level they need. A function above the chosen level stays locked.',
    noFunctions: 'No module offers AI functions yet.',
    columns: {
      function: 'Function',
      data: 'Data',
      usage: 'Last 30 days',
      enabled: 'Switched on',
    },
    usageLine_one: '{{calls}} call, {{tokens}} tokens',
    usageLine_other: '{{calls}} calls, {{tokens}} tokens',
    functionEnabled: '{{name}} switched on',
    save: 'Save',
    saved: 'AI settings saved.',
    errors: {
      'ai-unavailable': 'The provider cannot be reached or rejects the key. Check key and model.',
      'ai-failed': 'The provider returned no usable answer.',
      'ai-busy': 'Too many requests right now. Try again in a moment.',
      'ai-budget-exhausted': 'The monthly limit of the company is reached.',
      'key-required': 'When changing the provider, enter the key again or remove it.',
    },
  },
  companies: {
    title: 'Companies',
    description: 'Every company is a separate tenant. Subgroups only arrange them and see no data.',
    newCompany: 'New company',
    newGroup: 'New subgroup',
    kind: { company: 'Company', group: 'Subgroup' },
    columns: { name: 'Name', kind: 'Kind', slug: 'Short code', status: 'Status' },
    status: { active: 'Active', disabled: 'Disabled' },
    empty: 'No companies yet',
    name: 'Name',
    shortName: 'Short name',
    shortNameHint: 'In the company switcher, e.g. STE.',
    slug: 'Short code in addresses',
    slugHint: 'Lower-case letters, digits and hyphens, at least two characters.',
    parent: 'Parent',
    noParent: 'Top level',
    color: 'Company colour',
    createCompany: 'Create company',
    createGroup: 'Create subgroup',
    created: 'Created',
    edit: 'Edit',
    editTitle: 'Edit {{name}}',
    saved: 'Saved',
    disable: 'Disable',
    enable: 'Enable',
    disableTitle: 'Disable {{name}}?',
    disableDescription:
      'All sessions in this company end at once and nobody can sign in any more. The data is kept.',
    disabled: 'Disabled',
    enabled: 'Enabled',
    invalidParent: 'This placement is not possible: a company cannot be below itself.',
    slugTaken: 'This short code is already taken.',
  },
  modules: {
    title: 'Modules',
    description:
      'The switch "All companies" is the default. Every company can switch modules on and off itself under Administration › Modules, its switch wins.',
    empty: 'No modules installed yet',
    emptyHint: 'Modules come into the app as packages and then appear here.',
    global: 'All companies',
    company: 'Company',
    inherit: 'Like all companies',
    on: 'On',
    off: 'Off',
    groupScope: 'Group-wide',
    submoduleOf: 'Submodule of {{parent}}',
    dependsOn: 'Needs {{modules}}',
    saved: 'Saved',
    perCompany: 'Per company',
  },
  companyModules: {
    title: 'Modules',
    description:
      'Which modules the company uses and in which order they appear in the sidebar and on the start page. Without its own choice the default of the platform applies. Modules switched off disappear from navigation and search, their data is kept.',
    standardOn: 'Default: on',
    standardOff: 'Default: off',
    own: 'Own choice',
    reset: 'Use default',
    needs: 'Takes effect once {{modules}} is on',
    saved: 'Saved',
    moveUp: 'Move “{{name}}” up',
    moveDown: 'Move “{{name}}” down',
  },
  platformAccess: {
    title: 'Platform permissions',
    description: 'Permissions above all companies. Few people, MFA only, every change is logged.',
    grants: 'Granted platform permissions',
    grantsHint: 'Only another superadmin can grant and revoke.',
    grant: 'Grant permission',
    grantTitle: 'Grant platform permission',
    granted: 'Granted',
    revoke: 'Revoke {{permission}} from {{name}}',
    revokeTitle: 'Revoke {{permission}}?',
    revokeDescription: '{{name}} loses the permission at once.',
    revokeConfirm: 'Revoke',
    revoked: 'Revoked',
    noGrants: 'No platform permissions granted yet.',
    company: 'Company',
    chooseCompany: 'Choose company',
    person: 'Person',
    choosePerson: 'Choose person',
    searchPerson: 'Search name',
    permission: 'Permission',
    grantedBy: 'Granted by',
    grantedAt: 'Granted on',
    cannotGrantSelf: 'Another superadmin grants your own platform permissions.',
    cannotRevokeSelf: 'Another superadmin revokes your own superadmin permission.',
    links: 'Linked accounts',
    linksHint:
      'Connects the accounts of one person in two companies. The company switcher then shows the other company directly.',
    link: 'Link accounts',
    linkTitle: 'Link accounts',
    linked: 'Linked',
    first: 'First account',
    second: 'Second account',
    unlink: 'Remove link',
    unlinkTitle: 'Remove link?',
    unlinkDescription: 'Switching company then needs a separate sign-in.',
    unlinked: 'Link removed',
    noLinks: 'No accounts linked yet.',
    sameCompany: 'Please choose two different companies.',
  },
  operations: {
    title: 'Operations',
    description:
      'State of the platform for all companies. Counts only, no personal data. Refreshes every 30 seconds.',
    refresh: 'Refresh',
    justNow: 'just now',
    healthy: 'Nothing unusual',
    problems: {
      title: 'Needs attention',
      migrations_one: '{{count}} migration has not been applied yet.',
      migrations_other: '{{count}} migrations have not been applied yet.',
      jobs_one: '{{count}} job failed for good in the last 24 hours.',
      jobs_other: '{{count}} jobs failed for good in the last 24 hours.',
      events_one: '{{count}} event delivery failed in the last 24 hours.',
      events_other: '{{count}} event deliveries failed in the last 24 hours.',
      errors_one: '{{count}} unexpected error since the server started.',
      errors_other: '{{count}} unexpected errors since the server started.',
    },
    server: {
      title: 'Server',
      version: 'Version',
      commit: 'Commit',
      started: 'Started',
      errors: 'Unexpected errors since start',
    },
    database: {
      title: 'Database',
      status: 'Status',
      reachable: 'Reachable, {{ms}} ms',
      connections: 'Connections',
      pool: '{{total}} open, {{idle}} idle, {{waiting}} waiting',
      migrations: 'Migrations',
      applied: 'All {{count}} applied',
      pending: '{{count}} pending',
    },
    events: {
      title: 'Events',
      description: 'Delivery to the handlers in kernel and modules.',
      due: 'Due',
      scheduled: 'Waiting for another attempt',
      failed: 'Failed (24 hours)',
      oldest: 'Oldest due',
    },
    notifications: {
      title: 'Notification delivery',
      description: 'Errors since this server started.',
      email: 'E-mail',
      teams: 'Microsoft Teams',
      push: 'Push messages',
    },
    ai: {
      title: 'AI today',
      description: 'All companies together, since midnight.',
      calls: 'Calls',
      failures: 'Of which failed',
      tokens: 'Tokens',
    },
    jobs: {
      title: 'Jobs',
      description: 'Queue by type. Failed for good means: all attempts used up.',
      type: 'Type',
      due: 'Due',
      running: 'Running',
      scheduled: 'Scheduled',
      failed: 'Failed (24 h)',
      empty: 'No jobs set up',
    },
    backups: {
      title: 'Database backups',
      description: 'Encrypted copies of the database, made by the server. Newest first.',
      create: 'Create backup now',
      confirmTitle: 'Create a backup now?',
      confirmDescription:
        'The server backs up the database right away. This usually takes a few minutes; the STAW App stays usable.',
      requestedToast: 'Backup requested',
      pending: 'A backup is already running or requested.',
      unavailable: 'Backups exist on the server only.',
      failed: 'The backup status cannot be loaded right now.',
      notAvailable: 'No backups in this environment',
      notAvailableHint: 'Database backups exist on the server only, not in local development.',
      state: 'Status',
      running: 'Running',
      requested: 'Requested',
      last: 'Last backup',
      lastSize: 'Size',
      none: 'None yet',
      updatedAt: 'As of',
      result: { success: 'Succeeded', failure: 'Failed', running: 'Running' },
      list: 'Available backups',
      createdAt: 'Created',
      name: 'File',
      size: 'Size',
      empty: 'No backups yet',
    },
  },
};
